import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Autocomplete,
    MenuItem as MuiMenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { toast } from "react-toastify";
import { firebaseCache } from "../../utils/firebaseCache";
import { updatePolicy } from "../policies/policySlice";
import { useSelector, useDispatch } from "react-redux"; // Added useDispatch
import { incrementActionCounter } from "../../utils/actionCounters";

const MobileCommissionEditDialog = ({ open, onClose, policy, onUpdate, userId }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [formData, setFormData] = useState({
        tpPremium: "",
        odPremium: "",
        premiumAmount: "",
        netPremium: "",
        discount: "",
        paidAmount: "",
        commissionRate: "0",
        tdsRate: "2",
        totalCommission: "",
        modeOfPayment: "",
        remarks: "",
    });
    const [paymentModes] = useState(["Cash", "UPI", "UPI+Cash", "Cheque", "Online Transfer", "Online Transfer+Cash"]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    // TextField styling for mobile
    const textFieldStyles = {
        width: "100%",
        "& .MuiInputBase-root": {
            height: "44px",
            borderRadius: "8px",
            fontSize: "14px",
            backgroundColor: "#FFFFFF",
            "& fieldset": {
                borderColor: "#E0E0E0",
                borderWidth: "1px",
            },
            "&:hover fieldset": {
                borderColor: "#BDBDBD",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#0063F7",
                borderWidth: "2px",
            },
        },
        "& .MuiInputBase-input": {
            padding: "10px 12px",
        },
        "& .MuiInputLabel-root": {
            fontSize: "13px",
            color: "#757575",
            "&.Mui-focused": {
                color: "#0063F7",
            },
        },
        "& .MuiInputLabel-shrink": {
            backgroundColor: "#FFFFFF",
            padding: "0 4px",
        }
    };

    useEffect(() => {
        if (policy) {
            setFormData({
                tpPremium: policy.tpPremium || "",
                odPremium: policy.odPremium || "",
                premiumAmount: policy.premiumAmount || "",
                netPremium: policy.netPremium || "",
                discount: policy.discount || "0",
                amountPayable: policy.amountPayable || policy.premiumAmount || "",
                paidAmount: policy.paidAmount || "",
                commissionRate: policy.commissionRate || "0",
                odCommissionRate: policy.odCommissionRate || "0",
                odCommissionAmount: policy.odCommissionAmount || "0.00",
                tpCommissionRate: policy.tpCommissionRate || "0",
                tpCommissionAmount: policy.tpCommissionAmount || "0.00",
                tdsRate: policy.tdsRate || "2",
                totalCommission: Math.max(0, parseFloat(policy.totalCommission || 0)).toFixed(2),
                modeOfPayment: policy.modeOfPayment || "",
                remarks: policy.remarks || "",
            });
        }
    }, [policy]);

    // Auto-calculation logic
    useEffect(() => {
        const netPremium = parseFloat(formData.netPremium) || 0;
        const tds = parseFloat(formData.tdsRate) || 0;
        let grossCommission = 0;

        if (policy?.policyCategory === 'motor') {
            const odAmount = parseFloat(formData.odCommissionAmount) || 0;
            const tpAmount = parseFloat(formData.tpCommissionAmount) || 0;
            grossCommission = odAmount + tpAmount;
        } else {
            const rate = parseFloat(formData.commissionRate) || 0;
            grossCommission = netPremium * (rate / 100);
        }

        // Total Commission = Gross Commission * (1 - TDS%)
        const total = Math.max(0, grossCommission * (1 - (tds / 100)));

        setFormData(prev => ({
            ...prev,
            totalCommission: total.toFixed(2)
        }));
    }, [formData.netPremium, formData.commissionRate, formData.odCommissionAmount, formData.tpCommissionAmount, formData.tdsRate, policy?.policyCategory]);

    const handleRateAdjustment = (field, adjustment) => {
        setFormData(prev => {
            const currentValue = parseFloat(prev[field]) || 0;
            const newValue = Math.max(0, currentValue + adjustment);
            const newData = {
                ...prev,
                [field]: newValue.toString()
            };

            // Recalculate commission amounts when rates change
            if (policy?.policyCategory === 'motor') {
                const odPremium = parseFloat(newData.odPremium) || 0;
                const tpPremium = parseFloat(newData.tpPremium) || 0;

                if (field === 'odCommissionRate') {
                    newData.odCommissionAmount = (odPremium * (newValue / 100)).toFixed(2);
                } else if (field === 'tpCommissionRate') {
                    newData.tpCommissionAmount = (tpPremium * (newValue / 100)).toFixed(2);
                }
            }

            return newData;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        // Apply input restrictions for amount fields
        if (['premiumAmount', 'netPremium', 'paidAmount', 'discount', 'tpPremium', 'odPremium', 'totalCommission', 'odCommissionAmount', 'tpCommissionAmount'].includes(name)) {
            // Only allow digits and decimal point
            processedValue = value.replace(/[^0-9.]/g, '');
            // Prevent multiple decimal points
            const decimalCount = (processedValue.match(/\./g) || []).length;
            if (decimalCount > 1) {
                const parts = processedValue.split('.');
                processedValue = parts[0] + '.' + parts.slice(1).join('');
            }
            // Limit to 2 decimal places
            if (processedValue.includes('.')) {
                const parts = processedValue.split('.');
                if (parts[1] && parts[1].length > 2) {
                    processedValue = parts[0] + '.' + parts[1].substring(0, 2);
                }
            }
        }

        setFormData(prev => {
            const newData = { ...prev, [name]: processedValue };

            // Payment logic
            if (name === 'premiumAmount' || name === 'discount') {
                const total = parseFloat(newData.premiumAmount) || 0;
                const disc = parseFloat(newData.discount) || 0;
                newData.amountPayable = (total - disc).toFixed(2);
            }

            // Motor Commission logic
            if (policy?.policyCategory === 'motor') {
                const odPremium = parseFloat(newData.odPremium) || 0;
                const tpPremium = parseFloat(newData.tpPremium) || 0;

                if (name === 'odCommissionRate') {
                    const rate = parseFloat(newData.odCommissionRate) || 0;
                    newData.odCommissionAmount = (odPremium * (rate / 100)).toFixed(2);
                } else if (name === 'odCommissionAmount') {
                    const amount = parseFloat(newData.odCommissionAmount) || 0;
                    newData.odCommissionRate = odPremium > 0 ? ((amount / odPremium) * 100).toFixed(2) : "0";
                } else if (name === 'tpCommissionRate') {
                    const rate = parseFloat(newData.tpCommissionRate) || 0;
                    newData.tpCommissionAmount = (tpPremium * (rate / 100)).toFixed(2);
                } else if (name === 'tpCommissionAmount') {
                    const amount = parseFloat(newData.tpCommissionAmount) || 0;
                    newData.tpCommissionRate = tpPremium > 0 ? ((amount / tpPremium) * 100).toFixed(2) : "0";
                }
            }

            return newData;
        });
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            if (!userId) throw new Error("User ID missing");

            const updates = {
                tpPremium: formData.tpPremium,
                odPremium: formData.odPremium,
                premiumAmount: formData.premiumAmount,
                totalAmount: formData.premiumAmount,
                netPremium: formData.netPremium,
                discount: formData.discount,
                amountPayable: formData.amountPayable,
                paidAmount: formData.paidAmount,
                receivedAmount: formData.paidAmount,
                commissionRate: formData.commissionRate,
                odCommissionRate: formData.odCommissionRate,
                odCommissionAmount: formData.odCommissionAmount,
                tpCommissionRate: formData.tpCommissionRate,
                tpCommissionAmount: formData.tpCommissionAmount,
                tdsRate: formData.tdsRate,
                totalCommission: formData.totalCommission,
                modeOfPayment: formData.modeOfPayment,
                remarks: formData.remarks,
                grossCommission: (policy?.policyCategory === 'motor') ?
                    (parseFloat(formData.odCommissionAmount) + parseFloat(formData.tpCommissionAmount)) :
                    ((parseFloat(formData.netPremium) * parseFloat(formData.commissionRate)) / 100)
            };

            await dispatch(updatePolicy({ agentId: userId, id: policy.id, updates })).unwrap();

            toast.success("Policy updated successfully!", {
                containerId: "bottom-center",
                position: "bottom-center",
                autoClose: 2000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
                closeButton: false,
                style: {
                    backgroundColor: '#166534', // Dark green theme
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    padding: '10px 20px', // Symmetric padding
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center', // Centered text
                    textAlign: 'center',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    marginBottom: '100px', // Lifted above the FAB/Dialog position
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    maxWidth: '90vw',
                },
                icon: false, // Remove icon to ensure perfect text centering
            });

            // Increment commission edit counter for admin users
            if (user?.isPhoneUser) {
                await incrementActionCounter(user.uid, 'commsioneditcounter');
            }

            // Invalidate cache to ensure fresh data on refresh
            firebaseCache.invalidateAfterWrite('policydetails_write', userId);
            if (onUpdate) onUpdate();
            handleClose();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update commission.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 200); // Match animation duration
    };

    // Prevent background scroll when open (Robust solution for iOS)
    useEffect(() => {
        if (open) {
            // Save current scroll position
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        } else {
            // Restore scroll position
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = 'unset';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }
        return () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = 'unset';
        };
    }, [open]);

    // Click outside to close
    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (e) => {
            const filterBox = document.querySelector('[data-commission-edit-box]');

            // Check if click is on Autocomplete dropdown (MUI poppers/listbox)
            const isPopper = e.target.closest('.MuiAutocomplete-popper') ||
                e.target.closest('.MuiAutocomplete-listbox') ||
                e.target.closest('.MuiPopover-root') ||
                e.target.closest('.MuiMenu-root');

            if (filterBox && !filterBox.contains(e.target) && !isPopper) {
                handleClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    if (!open && !isClosing) return null;

    return (
        <Box
            data-commission-edit-box
            sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: isClosing
                    ? 'translate(-50%, -50%) scale(0.3)'
                    : 'translate(-50%, -50%) scale(1)',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                zIndex: 1300,
                width: '92vw',
                maxWidth: 400,
                maxHeight: '90vh', // Changed from 85vh to 90vh
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                animation: isClosing
                    ? 'collapseScale 0.2s ease-in forwards'
                    : 'expandScale 0.3s ease-out',
                '@keyframes expandScale': {
                    '0%': {
                        opacity: 0,
                        transform: 'translate(-50%, -50%) scale(0.3)',
                    },
                    '100%': {
                        opacity: 1,
                        transform: 'translate(-50%, -50%) scale(1)',
                    }
                },
                '@keyframes collapseScale': {
                    '0%': {
                        opacity: 1,
                        transform: 'translate(-50%, -50%) scale(1)',
                    },
                    '100%': {
                        opacity: 0,
                        transform: 'translate(-50%, -50%) scale(0.3)',
                    }
                }
            }}
        >
            {/* Header - matching AddMeeting style */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                pb: 1
            }}>
                <Typography
                    variant="h9"
                    sx={{
                        fontFamily: 'Roboto Slab',
                        fontWeight: 500,
                        fontSize: { xs: '1.1rem', sm: '1.375rem', md: '1.5rem' },
                        lineHeight: { xs: '1.5', sm: '1.6' },
                        letterSpacing: '1%',
                        color: '#212121',
                    }}
                >
                    Edit Commission
                </Typography>
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        color: '#666',
                        '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            {/* Content - SCROLLABLE AREA */}
            <Box sx={{
                p: 2,
                pt: 0.5,
                overflowY: 'auto',
                flex: 1,
                WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                '&::-webkit-scrollbar': {
                    width: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#E0E0E0',
                    borderRadius: '4px',
                }
            }}>
                {/* Payment Details Section */}
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{
                        variant: "h9",
                        fontFamily: 'Roboto',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#212121',
                        mb: 2.2
                    }}>
                        Payment Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {policy?.policyCategory === "motor" && (
                            <>
                                {policy.policyType !== "Liability" && (
                                    <TextField
                                        label="OD Premium"
                                        name="odPremium"
                                        value={formData.odPremium}
                                        onChange={handleChange}
                                        size="small"
                                        sx={textFieldStyles}
                                    />
                                )}
                                {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                                    <TextField
                                        label="TP Premium"
                                        name="tpPremium"
                                        value={formData.tpPremium}
                                        onChange={handleChange}
                                        size="small"
                                        sx={textFieldStyles}
                                    />
                                )}
                            </>
                        )}
                        <TextField
                            label="Net Premium"
                            name="netPremium"
                            value={formData.netPremium}
                            onChange={handleChange}
                            size="small"
                            sx={textFieldStyles}
                        />
                        <TextField
                            label="Total Premium"
                            name="premiumAmount"
                            value={formData.premiumAmount}
                            onChange={handleChange}
                            size="small"
                            sx={textFieldStyles}
                        />
                        <TextField
                            label="Customer Discount"
                            name="discount"
                            value={formData.discount}
                            onChange={handleChange}
                            size="small"
                            sx={textFieldStyles}
                        />
                        <TextField
                            label="Amount Payable"
                            name="amountPayable"
                            value={formData.amountPayable}
                            onChange={handleChange}
                            size="small"
                            sx={textFieldStyles}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <TextField
                            label="Amount Received"
                            name="paidAmount"
                            value={formData.paidAmount}
                            onChange={handleChange}
                            size="small"
                            sx={textFieldStyles}
                        />
                        <Autocomplete
                            options={paymentModes}
                            value={formData.modeOfPayment}
                            onChange={(e, newValue) => {
                                setFormData(prev => ({ ...prev, modeOfPayment: newValue || "" }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Mode of Payment"
                                    sx={textFieldStyles}
                                />
                            )}
                            popupIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                        />
                        <TextField
                            label="Remarks"
                            name="remarks"
                            value={formData.remarks}
                            onChange={handleChange}
                            multiline
                            rows={2}
                            sx={{
                                ...textFieldStyles,
                                "& .MuiInputBase-root": {
                                    ...textFieldStyles["& .MuiInputBase-root"],
                                    height: 'auto',
                                }
                            }}
                        />
                    </Box>
                </Box>

                {/* Commission Details Section */}
                <Box sx={{ mb: 1 }}>
                    <Typography sx={{
                        fontFamily: 'Roboto',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#212121',
                        mb: 2.2
                    }}>
                        Commission Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {policy?.policyCategory === 'motor' ? (
                            <>
                                {policy.policyType !== "Liability" && (
                                    <>
                                        <TextField
                                            label="OD Commission Rate (%)"
                                            name="odCommissionRate"
                                            type="number"
                                            value={formData.odCommissionRate}
                                            onChange={handleChange}
                                            size="small"
                                            sx={textFieldStyles}
                                            inputProps={{ inputMode: "numeric" }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRateAdjustment('odCommissionRate', -1)}
                                                            sx={{ padding: '4px', color: '#757575' }}
                                                        >
                                                            <RemoveIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRateAdjustment('odCommissionRate', 1)}
                                                            sx={{ padding: '4px', color: '#757575' }}
                                                        >
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <TextField
                                            label="OD Commission Amount"
                                            name="odCommissionAmount"
                                            value={formData.odCommissionAmount}
                                            onChange={handleChange}
                                            size="small"
                                            sx={textFieldStyles}
                                            inputProps={{
                                                inputMode: "decimal"
                                            }}
                                        />
                                    </>
                                )}
                                {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                                    <>
                                        <TextField
                                            label="TP Commission Rate (%)"
                                            name="tpCommissionRate"
                                            type="number"
                                            value={formData.tpCommissionRate}
                                            onChange={handleChange}
                                            size="small"
                                            sx={textFieldStyles}
                                            inputProps={{ inputMode: "numeric" }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRateAdjustment('tpCommissionRate', -1)}
                                                            sx={{ padding: '4px', color: '#757575' }}
                                                        >
                                                            <RemoveIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRateAdjustment('tpCommissionRate', 1)}
                                                            sx={{ padding: '4px', color: '#757575' }}
                                                        >
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <TextField
                                            label="TP Commission Amount"
                                            name="tpCommissionAmount"
                                            value={formData.tpCommissionAmount}
                                            onChange={handleChange}
                                            size="small"
                                            sx={textFieldStyles}
                                            inputProps={{
                                                inputMode: "decimal"
                                            }}
                                        />
                                    </>
                                )}
                            </>
                        ) : (
                            <TextField
                                label="Commission Rate (%)"
                                name="commissionRate"
                                type="number"
                                value={formData.commissionRate}
                                onChange={handleChange}
                                size="small"
                                sx={textFieldStyles}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRateAdjustment('commissionRate', -1)}
                                                sx={{ padding: '4px', color: '#757575' }}
                                            >
                                                <RemoveIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRateAdjustment('commissionRate', 1)}
                                                sx={{ padding: '4px', color: '#757575' }}
                                            >
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        )}
                        <TextField
                            label="TDS Rate (%)"
                            name="tdsRate"
                            type="number"
                            value={formData.tdsRate}
                            onChange={handleChange}
                            size="small"
                            sx={textFieldStyles}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRateAdjustment('tdsRate', -1)}
                                            sx={{ padding: '4px', color: '#757575' }}
                                        >
                                            <RemoveIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRateAdjustment('tdsRate', 1)}
                                            sx={{ padding: '4px', color: '#757575' }}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <TextField
                            label="Total Commission"
                            name="totalCommission"
                            value={formData.totalCommission}
                            onChange={handleChange}
                            size="small"
                            sx={{
                                ...textFieldStyles,
                                "& .MuiInputBase-input": {
                                    color: '#166534',
                                    fontWeight: 'bold'
                                }
                            }}
                            inputProps={{
                                inputMode: "decimal"
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Footer Buttons */}
            <Box sx={{
                px: 2,
                py: 1.5,
                backgroundColor: '#FFFFFF',
                borderTop: '1px solid #f0f0f0',
                display: 'flex',
                gap: 1.5
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        flex: 1,
                        height: '40px',
                        borderRadius: '8px',
                        borderColor: '#B00020',
                        color: '#B00020',
                        fontFamily: 'Roboto',
                        fontWeight: 500,
                        fontSize: '14px',
                        textTransform: 'none',
                        "&:hover": {
                            borderColor: '#B00020',
                            backgroundColor: 'rgba(176, 0, 32, 0.04)'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                        flex: 1,
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: isSubmitting ? '#7e9ed8' : '#0063F7 !important',
                        color: '#FFFFFF',
                        fontFamily: 'Roboto',
                        fontWeight: 500,
                        fontSize: '14px',
                        textTransform: 'none',
                        "&:hover:not(:disabled)": {
                            backgroundColor: '#0051d4 !important',
                        },
                        "&:disabled": {
                            backgroundColor: '#7e9ed8 !important',
                            color: '#FFFFFF'
                        }
                    }}
                >
                    {isSubmitting ? "Updating..." : "Update"}
                </Button>
            </Box>
        </Box>
    );
};

export default MobileCommissionEditDialog;
